import React, { useCallback, useMemo } from "react";
import { RouteComponentProps } from "react-router";

import ContractEditor, {
  ContractEditorState,
  SigneeType,
  defaultSigneesAvailable,
} from "../components/contracts/ContractEditor";
import { ContractEditorProvider } from "../components/contracts/ContractEditorContext";
import { ContractIcon } from "../components/icons";
import {
  ScreenHeader,
  ScreenContainer,
  Container,
  EmptyState,
  Link,
} from "../components/wombat-ui";
import { GET_VIEWER } from "../graphql/hooks/queries/useGetViewer";
import useAddNotification from "../hooks/useAddNotification";

import useInsertContractTemplate from "../hooks/useInsertContractTemplate";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

type EditContractTemplateScreenProps = {} & RouteComponentProps<{ id: string }>;

const EditContractTemplateScreen: React.FC<EditContractTemplateScreenProps> = ({
  match,
}) => {
  const {
    isCapableOfInsertingContractTemplates,
    company: { id: companyId, contractTemplates },
  } = useViewer();

  const contract = useMemo(() => {
    return contractTemplates.find(({ id }) => id === match.params.id);
  }, [contractTemplates]);
  const t = useTranslations();
  const [insertContractTemplate] = useInsertContractTemplate({
    refetchQueries: [{query: GET_VIEWER}]
  });
  const [addNotification] = useAddNotification();

  const handleContractSaved = useCallback(
    (data: ContractEditorState & { signatories: SigneeType[] }) => {
      const { title, signatories, ...rest } = data;
      const serializedState = JSON.stringify(rest);

      insertContractTemplate({
        variables: {
          input: {
            id: contract.id,
            title,
            body: serializedState,
            signatories,
            companyId,
          },
        },
      })
        .then(() => {
          addNotification({
            variables: {
              title: t(
                "contracts.notifications.templateEdit.success.title"
              ) as string,
              body: t(
                "contracts.notifications.templateEdit.success.body"
              ) as string,
              status: "success",
            },
          });
        })
        .catch(() => {
          addNotification({
            variables: {
              title: t(
                "contracts.notifications.templateEdit.error.title"
              ) as string,
              body: t(
                "contracts.notifications.templateEdit.error.body"
              ) as string,
              status: "error",
            },
          });
        });
    },
    [insertContractTemplate, match.params.id]
  );

  const deserializedState = useMemo((): ContractEditorState | undefined => {
    if (contract)
      return {
        ...JSON.parse(contract.body),
        title: contract.title,
        signatories: defaultSigneesAvailable.filter(
          (s) => contract.signatories.indexOf(s) === -1
        ),
      };
    return undefined;
  }, [contract]);

  return (
    <ContractEditorProvider defaultState={deserializedState?.variables}>
      <ScreenContainer
        loading={!contract}
        error={
          !isCapableOfInsertingContractTemplates ? (
            <EmptyState
              title={t("network.unauthorized") as string}
              action={
                <Link variant="button" to="/">
                  {t("network.backToHome")}
                </Link>
              }
            />
          ) : undefined
        }
      >
        <ScreenHeader
          icon={ContractIcon}
          title={t("contracts.contractTemplateEdit")}
        />
        <Container>
          <ContractEditor
            editable
            onSubmit={handleContractSaved}
            state={deserializedState}
          />
        </Container>
      </ScreenContainer>
    </ContractEditorProvider>
  );
};

export default React.memo(EditContractTemplateScreen);
